import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Volume2, VolumeX, PlayCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { detectMedia } from "../lib/media";
import BigButton from "./BigButton";
import WeekTimeline from "./WeekTimeline";

// Same shape as buildAnonymousRelease() in PoemPlayer.jsx — Day 1 is the
// only thing an anonymous visitor can actually open, so this teaser's
// week-preview strip mirrors that exactly rather than promising anything
// this component can't back up. Static (doesn't depend on the fetched
// poem), so it lives outside the component instead of being rebuilt every
// render.
const TEASER_RELEASE = {
    current_day: 1,
    week_completed: false,
    days: {
        "1": { unlocked: true, unlocks_at: null, source: "free_preview", global_day: 1 },
        "2": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 2 },
        "3": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 3 },
        "4": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 4 },
        "5": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 5 },
        "6": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 6 },
        "7": { unlocked: false, unlocks_at: null, source: "sign_in_required", global_day: 7 },
    },
};

// Auto-playing muted preview of Poem #01 that starts on scroll.
// Falls back gracefully if the poem or media isn't fetched yet.
export default function MicroOnboarding() {
    const navigate = useNavigate();
    const [poem, setPoem] = useState(null);
    const [muted, setMuted] = useState(true);
    const [captionIdx, setCaptionIdx] = useState(0);
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const inView = useInView(containerRef, { amount: 0.4, once: false });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/poems/poem_01");
                setPoem(data);
            } catch { /* ignore */ }
        })();
    }, []);

    // Play/pause video based on visibility
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (inView) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked */ });
        } else {
            v.pause();
        }
    }, [inView, poem]);

    // Cycle captions every 3.2s while visible
    const captions = useMemo(() => {
        if (!poem?.transcript) return [];
        return poem.transcript.split("\n").map((l) => l.trim()).filter(Boolean);
    }, [poem]);

    useEffect(() => {
        if (!inView || captions.length < 2) return;
        const id = setInterval(() => setCaptionIdx((i) => (i + 1) % captions.length), 3200);
        return () => clearInterval(id);
    }, [inView, captions.length]);

    if (!poem) {
        return (
            <section className="max-w-6xl mx-auto px-6 py-14">
                <div className="rounded-[2rem] bg-white border border-honeySoft h-[380px] animate-pulse" />
            </section>
        );
    }

    const media = detectMedia(poem.video_url);
    const isNative = media.type === "direct";

    // Build the autoplay/muted embed URL for YouTube/Vimeo. Looping a single
    // YouTube video requires playlist=<same id> — an empty playlist param
    // makes YouTube render its "video unavailable" screen instead of looping.
    let embedSrc = media.src;
    if (media.type === "youtube") {
        const idMatch = media.src.match(/\/embed\/([^/?]+)/);
        const id = idMatch ? idMatch[1] : "";
        const params = new URLSearchParams({ rel: "0", autoplay: "1", mute: "1" });
        if (id) {
            params.set("loop", "1");
            params.set("playlist", id);
        }
        embedSrc = `https://www.youtube.com/embed/${id}?${params.toString()}`;
    } else if (media.type === "vimeo") {
        const embed = new URL(media.src);
        embed.searchParams.set("autoplay", "1");
        embed.searchParams.set("muted", "1");
        embed.searchParams.set("loop", "1");
        embedSrc = embed.href;
    }

    return (
        <section ref={containerRef} className="max-w-6xl mx-auto px-6 py-16 sm:py-20" data-testid="micro-onboarding">
            <div className="text-center max-w-2xl mx-auto mb-10">
                <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">A little peek</p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-2">No sign-in. Just press play.</h2>
                <p className="text-mocha mt-3">Here&apos;s the first poem, playing right now with the sound off. Turn it on when you&apos;re ready.</p>
            </div>

            {/* grid-cols-1 (not just md:grid-cols-5) matters here specifically
                because of the auto-scrolling word ticker below — without an
                explicit mobile track, this column has no width constraint at
                all and grows to fit the ticker's full un-clipped content
                width (it's deliberately wider than the screen, meant to be
                hidden by its own overflow-hidden wrapper), blowing the whole
                page out to several times the viewport width on phones. */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
                {/* Video / preview */}
                <div className="md:col-span-3">
                    <motion.div
                        initial={{ y: 16, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", stiffness: 90, damping: 16 }}
                        className="relative rounded-[2rem] overflow-hidden bg-charcoal shadow-xl shadow-deepGreen/10 border-4 border-white"
                    >
                        {isNative ? (
                            <video
                                ref={videoRef}
                                src={media.src}
                                muted={muted}
                                loop
                                playsInline
                                autoPlay
                                preload="metadata"
                                className="w-full aspect-video bg-black object-cover"
                                data-testid="micro-video"
                            />
                        ) : (
                            <iframe
                                src={embedSrc}
                                title={poem.title}
                                className="w-full aspect-video"
                                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                                data-testid="micro-video"
                            />
                        )}

                        {/* Overlay caption strip */}
                        {captions.length > 0 && (
                            <div className="absolute left-0 right-0 bottom-4 flex justify-center pointer-events-none px-4">
                                <motion.div
                                    key={captionIdx}
                                    initial={{ y: 8, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.35 }}
                                    className="rounded-2xl bg-white/95 backdrop-blur px-4 py-2 font-display text-base sm:text-lg font-semibold text-deepGreen shadow"
                                    data-testid="micro-caption"
                                >
                                    {captions[captionIdx]}
                                </motion.div>
                            </div>
                        )}

                        {/* Corner mute badge (only for native video where we control audio) */}
                        {isNative && (
                            <button
                                onClick={() => {
                                    const v = videoRef.current;
                                    if (!v) return;
                                    const next = !muted;
                                    setMuted(next);
                                    v.muted = next;
                                }}
                                className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-bold text-deepGreen inline-flex items-center gap-1 shadow"
                                data-testid="micro-mute-toggle"
                            >
                                {muted ? <VolumeX className="w-4 h-4" strokeWidth={2.6} /> : <Volume2 className="w-4 h-4" strokeWidth={2.6} />}
                                {muted ? "Sound off" : "Sound on"}
                            </button>
                        )}
                    </motion.div>

                    {/* Same week-timeline strip a signed-in learner sees inside the
                        poem itself, so a visitor can see the whole week's shape —
                        not just today's video — before ever signing up. Only Day 1
                        is actually open here, same as everywhere else an anonymous
                        visitor can reach; tapping any of the other six is a nudge
                        straight to registration rather than a locked-day dialog,
                        since there's no countdown to show someone who isn't signed
                        in yet. */}
                    <div className="mt-4">
                        <WeekTimeline
                            release={TEASER_RELEASE}
                            selectedDay={1}
                            onSelectDay={() => navigate(`/poem/${poem.poem_id}`)}
                            onLockedClick={() => navigate("/register")}
                            marquee
                        />
                    </div>
                </div>

                {/* Side: what just happened */}
                <div className="md:col-span-2 space-y-4">
                    <span className="inline-flex items-center gap-2 rounded-full bg-honeySoft px-4 py-1.5 text-sm font-bold text-deepGreen">
                        <Sparkles className="w-4 h-4" strokeWidth={2.8} />
                        Poem #{String(poem.order_index).padStart(2, "0")} · {poem.title}
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-deepGreen leading-tight">One tap. No account. No ads.</h3>
                    <ul className="space-y-3 text-mocha">
                        <li className="flex items-start gap-3">
                            <span className="rounded-full bg-brightGreen text-white w-6 h-6 flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                            <span>Watch the poem with subtitles that follow the rhythm.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="rounded-full bg-brightGreen text-white w-6 h-6 flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                            <span>Tap a picture card to hear each word out loud.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="rounded-full bg-brightGreen text-white w-6 h-6 flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                            <span>Play the picture quiz — three choices, gentle wins.</span>
                        </li>
                    </ul>
                    <div className="pt-2">
                        <BigButton onClick={() => navigate(`/poem/${poem.poem_id}`)} variant="primary" data-testid="micro-open-poem">
                            <PlayCircle className="w-5 h-5" strokeWidth={2.5} />
                            Open the full poem
                        </BigButton>
                    </div>
                </div>
            </div>
        </section>
    );
}
